// Create a no-op object for SSR
const noopPosthog = {
  capture: () => {},
  identify: () => {},
  reset: () => {},
  people: { set: () => {} },
  getFeatureFlag: () => false,
  isFeatureEnabled: () => false,
  init: () => {},
};

// Only import and initialize posthog on client side
let posthog: any = noopPosthog;

if (typeof window !== 'undefined') {
  // Use dynamic import to avoid SSR issues with posthog-js
  import('posthog-js').then((posthogModule) => {
    posthog = posthogModule.default;
    
    // Get PostHog config from environment variables
    // Use different keys for dev vs prod to separate data
    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
    const isDevelopment = import.meta.env.DEV || 
                          window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1';
    
    // Only initialize if we have a key
    if (!posthogKey || posthogKey === 'phc_placeholder_key') {
      console.warn('PostHog: No API key provided, skipping initialization');
      return;
    }
    
    posthog.init(posthogKey, {
      api_host: posthogHost,
      loaded: (ph: any) => {
        // Enable debug mode in development
        if (isDevelopment) {
          ph.debug();
        }
        
        // Register environment and app info
        ph.register({
          app_name: 'CampWhiz',
          app_version: '1.0.0',
          environment: isDevelopment ? 'development' : 'production',
          hostname: window.location.hostname
        });
        
        // With separate PostHog projects for dev/prod, we can capture to both
        // Dev events go to dev project, prod events go to prod project
        // This allows testing PostHog features in development
        // If you want to disable dev tracking entirely, uncomment the opt-out below:
        /*
        if (isDevelopment) {
          ph.opt_out_capturing();
          ph.set_config({ disable_session_recording: true });
          console.log('PostHog: Development mode - capturing disabled');
        }
        */
        
        // Ensure capturing is enabled (default, but explicit for clarity)
        ph.opt_in_capturing();
      },
      capture_pageview: true, // Capture pageviews in both dev and prod
      capture_pageleave: true, // Capture pageleave in both dev and prod
      disable_session_recording: isDevelopment, // Disable session recording in dev (optional)
      enable_recording_console_log: !isDevelopment, // Only in production
      persistence: 'localStorage'
    });
  }).catch((error) => {
    // If import fails, keep no-op
    console.warn('Failed to load PostHog:', error);
  });
}

export default posthog

// Helper functions for common tracking
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog !== noopPosthog) {
    posthog.capture(eventName, properties)
  }
}

export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog !== noopPosthog) {
    posthog.identify(userId, properties)
  }
}

export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog !== noopPosthog) {
    posthog.people.set(properties)
  }
}

export const getFeatureFlag = (flagKey: string) => {
  if (typeof window !== 'undefined' && posthog !== noopPosthog) {
    return posthog.getFeatureFlag(flagKey)
  }
  return false
}

export const isFeatureEnabled = (flagKey: string) => {
  if (typeof window !== 'undefined' && posthog !== noopPosthog) {
    return posthog.isFeatureEnabled(flagKey)
  }
  return false
}
